import type { TaskCategory, TaskTypeName } from '@savvy/constants/generated/gql-enums';

export const GLOBAL_TASK_CREATION_BASE_PATH = '/advisor/dashboard/tasks/new';

export const TASK_CREATION_SLUGS = {
  accountConfirmation: 'account-confirmation',
  accountMaintenance: 'account-maintenance',
  alternativeInvestmentsPurchase: 'alternative-investments-purchase',
  clientInfoChange: 'client-info-change',
  clientOnboarding: 'client-onboarding',
  fileUpload: 'file-upload',
  intake: 'intake',
  moneyMovement: 'money-movement',
  onboarding: 'onboarding',
  openNewAccount: 'open-new-account',
  opsOther: 'ops-other',
  otherClient: 'other-client',
  otherInvestmentTask: 'other-investment-task',
  preReviewCheckIn: 'prereview-checkin',
  riskQuestionnaire: 'risk-questionnaire',
  swim: 'swim',
  task: 'task',
  trading: 'trading',
  transitionAnalysis: 'transition-analysis',
  transferOfAssets: 'transfer-of-assets',
  clientBilling: 'client-billing',
  corporateRetirementPlan: 'corporate-retirement-plan',
} as const;

export type TaskCreationSlug = (typeof TASK_CREATION_SLUGS)[keyof typeof TASK_CREATION_SLUGS];

// Deliberately kept out of TASK_CREATION_SLUGS: the freeform entrypoint has no
// wizard registry entry, so slugFromHref returns null and the drawer falls back
// to full-page navigation instead of trying to host it inline.
export const FREEFORM_TASK_SLUG = 'freeform';

const TASK_TYPE_TO_CREATION_SLUG: Partial<Record<TaskTypeName, TaskCreationSlug>> = {
  account_maintenance: TASK_CREATION_SLUGS.accountMaintenance,
  account_setup: TASK_CREATION_SLUGS.accountConfirmation,
  alternative_investments_purchase: TASK_CREATION_SLUGS.alternativeInvestmentsPurchase,
  client_info_change: TASK_CREATION_SLUGS.clientInfoChange,
  client_other: TASK_CREATION_SLUGS.otherClient,
  file_upload: TASK_CREATION_SLUGS.fileUpload,
  general: TASK_CREATION_SLUGS.task,
  initial_intake_form: TASK_CREATION_SLUGS.intake,
  money_movement_request: TASK_CREATION_SLUGS.moneyMovement,
  onboarding_flow: TASK_CREATION_SLUGS.onboarding,
  open_new_account: TASK_CREATION_SLUGS.openNewAccount,
  other: TASK_CREATION_SLUGS.task,
  other_investment_task: TASK_CREATION_SLUGS.otherInvestmentTask,
  pre_review_check_in: TASK_CREATION_SLUGS.preReviewCheckIn,
  risk_questionnaire: TASK_CREATION_SLUGS.riskQuestionnaire,
  self_reminder: TASK_CREATION_SLUGS.task,
  swim_request: TASK_CREATION_SLUGS.swim,
  trading_request: TASK_CREATION_SLUGS.trading,
  transition_analysis: TASK_CREATION_SLUGS.transitionAnalysis,
  transfer_of_assets: TASK_CREATION_SLUGS.transferOfAssets,
  client_billing: TASK_CREATION_SLUGS.clientBilling,
  corporate_retirement_plan: TASK_CREATION_SLUGS.corporateRetirementPlan,
};

// Types whose form differs by category. `other` is one type spanning two forms: the ops variant
// carries fields the general task form has no inputs for (Client Services assignee, instructions,
// Box folder), and it is the majority — 5,692 of 8,410 `other` rows are ops.
const OPS_CATEGORY_CREATION_SLUG: Partial<Record<TaskTypeName, TaskCreationSlug>> = {
  other: TASK_CREATION_SLUGS.opsOther,
};

const TASK_SUGGESTION_TYPE_TO_CREATION_SLUG: Partial<Record<TaskTypeName, TaskCreationSlug>> = {
  account_maintenance: TASK_CREATION_SLUGS.accountMaintenance,
  account_setup: TASK_CREATION_SLUGS.accountConfirmation,
  client_info_change: TASK_CREATION_SLUGS.clientInfoChange,
  client_other: TASK_CREATION_SLUGS.otherClient,
  file_upload: TASK_CREATION_SLUGS.fileUpload,
  financial_planning: TASK_CREATION_SLUGS.task,
  initial_intake_form: TASK_CREATION_SLUGS.intake,
  other: TASK_CREATION_SLUGS.opsOther,
  other_investment_task: TASK_CREATION_SLUGS.otherInvestmentTask,
  pre_review_check_in: TASK_CREATION_SLUGS.preReviewCheckIn,
  self_reminder: TASK_CREATION_SLUGS.task,
};

type GetTaskCreationHrefParams = {
  readonly salesforceAccountId?: string | null;
  readonly slug: TaskCreationSlug;
};

type GetTaskSuggestionCreationSlugParams = {
  readonly advisorDirectedOnboarding: boolean;
  readonly type: TaskTypeName;
};

export function getClientTaskCreationBasePath(salesforceAccountId: string) {
  return `/advisor/dashboard/client/${salesforceAccountId}/new-task`;
}

export function getTaskCreationSlug(type: TaskTypeName, category?: TaskCategory | null) {
  if (category === 'ops') {
    const opsSlug = OPS_CATEGORY_CREATION_SLUG[type];
    if (opsSlug !== undefined) return opsSlug;
  }

  return TASK_TYPE_TO_CREATION_SLUG[type];
}

export function getTaskSuggestionCreationSlug({
  advisorDirectedOnboarding,
  type,
}: GetTaskSuggestionCreationSlugParams) {
  if (type === 'onboarding_flow') {
    return advisorDirectedOnboarding ? TASK_CREATION_SLUGS.onboarding : TASK_CREATION_SLUGS.clientOnboarding;
  }

  return TASK_SUGGESTION_TYPE_TO_CREATION_SLUG[type];
}

export function getTaskCreationHref({ salesforceAccountId, slug }: GetTaskCreationHrefParams) {
  if (salesforceAccountId === undefined || salesforceAccountId === null || salesforceAccountId === '') {
    return `${GLOBAL_TASK_CREATION_BASE_PATH}?type=${slug}`;
  }

  return `${getClientTaskCreationBasePath(salesforceAccountId)}/${slug}`;
}

type GetTaskFormHrefParams = {
  readonly householdUuid?: string;
  readonly salesforceAccountId?: string;
  readonly slug: TaskCreationSlug;
};

/**
 * A type's own creation form, as opposed to `getTaskCreationHref`, which lands on the picker with the
 * card pre-selected when there's no household in the path.
 *
 * Mirrors the two route families: the client-scoped page when the advisor is already inside a household,
 * the global one carrying the household as a query param otherwise.
 */
export function getTaskFormHref({ householdUuid, salesforceAccountId, slug }: GetTaskFormHrefParams) {
  if (salesforceAccountId !== undefined && salesforceAccountId !== '') {
    return `${getClientTaskCreationBasePath(salesforceAccountId)}/${slug}`;
  }

  const formPath = `${GLOBAL_TASK_CREATION_BASE_PATH}/${slug}`;
  if (householdUuid === undefined || householdUuid === '') return formPath;

  return `${formPath}?householdUuid=${encodeURIComponent(householdUuid)}`;
}

/** Appends a `returnHref` query param consumed by `useAdvisorClientTaskExitNav`. */
export function withReturnHref(href: string, returnHref: string | undefined) {
  if (returnHref === undefined || returnHref === '') {
    return href;
  }
  const separator = href.includes('?') ? '&' : '?';
  return `${href}${separator}returnHref=${encodeURIComponent(returnHref)}`;
}
